import api from '@/api/base';
import type { ApiResponse } from '@/api/types';

// API URLs for call center operations
const URLS = {
  clients: '/callhistory/clients',
  clientById: (id: string) => `/callhistory/clients/${id}`,
  callbacks: '/callMeLater',
  callbackById: (id: string) => `/callMeLater/${id}`,
  callHistory: '/callhistory',
  callHistoryById: (id: string) => `/callhistory/${id}`,
} as const;

// The call-center controllers (call_history / call_me_later, via the shared
// responseFunction factory) return the { status, data } envelope. Concrete
// record shapes are not modeled here, so `data` stays `unknown`.
type CallCenterPayload = Record<string, unknown>;

// Call Center API methods
const callCenterApi = {
  // Get all clients — enveloped array
  getClients: (token?: string) => {
    return api.get<ApiResponse<unknown[]>>(URLS.clients, { token });
  },

  // Get client by ID — enveloped array of call histories
  getClientById: (id: string, token?: string) => {
    return api.get<ApiResponse<unknown[]>>(URLS.clientById(id), { token });
  },

  // Get all callback requests — enveloped array
  getCallbacks: (token?: string) => {
    return api.get<ApiResponse<unknown[]>>(URLS.callbacks, { token });
  },

  // Get callback by ID — enveloped object
  getCallbackById: (id: string, token?: string) => {
    return api.get<ApiResponse<unknown>>(URLS.callbackById(id), { token });
  },

  // Create callback request — enveloped object
  createCallback: (callbackData: CallCenterPayload, token?: string) => {
    return api.post<ApiResponse<unknown>>(URLS.callbacks, callbackData, {
      token,
    });
  },

  // Update callback request — enveloped object
  updateCallback: (
    id: string,
    callbackData: CallCenterPayload,
    token?: string,
  ) => {
    return api.patch<ApiResponse<unknown>>(
      URLS.callbackById(id),
      callbackData,
      { token },
    );
  },

  // Delete callback request — enveloped null
  deleteCallback: (id: string, token?: string) => {
    return api.delete<ApiResponse<null>>(URLS.callbackById(id), { token });
  },

  // Get call history — enveloped array
  getCallHistory: (token?: string) => {
    return api.get<ApiResponse<unknown[]>>(URLS.callHistory, { token });
  },

  // Get call history by ID — enveloped object
  getCallHistoryById: (id: string, token?: string) => {
    return api.get<ApiResponse<unknown>>(URLS.callHistoryById(id), { token });
  },

  // Create call history entry — enveloped object
  createCallHistory: (historyData: CallCenterPayload, token?: string) => {
    return api.post<ApiResponse<unknown>>(URLS.callHistory, historyData, {
      token,
    });
  },
};

export default callCenterApi;
